import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import theme from '@/constants/theme';
import { ContentType } from '@/services/contents/types';
import { useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';

interface SubmitFileProps {
  file: string | File | null;
  setFile: React.Dispatch<React.SetStateAction<string | File | null>>;
  contentType: ContentType;
  setDuration: React.Dispatch<React.SetStateAction<number | string>>;
}

export const SubmitFile: React.FC<SubmitFileProps> = ({ file, setFile, contentType, setDuration }) => {
  const acceptFileTypes: Record<ContentType, { accept?: string; label?: string }> = useMemo(
    () => ({
      VIDEO: {
        accept: 'video/*',
        label: 'Video',
      },
      AUDIO: {
        accept: 'audio/*',
        label: 'Audio',
      },
      IMAGE: {
        accept: 'image/*',
        label: 'Imagen',
      },
      READING: {},
      QUIZ: {},
    }),
    [],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      file && setFile(file);

      // get and set duration only for audio and video files
      if ((['AUDIO', 'VIDEO'] as ContentType[]).includes(contentType) && file) {
        const media = document.createElement(file.type.startsWith('audio') ? 'audio' : 'video');
        media.preload = 'metadata';

        media.onloadedmetadata = () => {
          const duration = media.duration;
          URL.revokeObjectURL(media.src);
          setDuration(Math.floor(duration));
        };

        media.src = URL.createObjectURL(file);
      }
    },
    [contentType, setDuration, setFile],
  );

  const acceptedFileTypes = useMemo(() => {
    if (contentType === 'READING' || contentType === 'QUIZ') {
      return '';
    }
    return acceptFileTypes[contentType].accept;
  }, [acceptFileTypes, contentType]);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    noClick: true,
    accept: {
      ...(acceptedFileTypes && { [acceptedFileTypes]: [] }),
    },
  });

  useEffect(() => {
    if (fileRejections && fileRejections.length > 0) {
      enqueueSnackbar({
        variant: 'error',
        message: 'Se exedió la cantidad máxima de archivos o el formato no es válido',
      });
    }
  }, [fileRejections]);

  if (contentType === 'READING' || contentType === 'QUIZ') {
    return null;
  }

  return (
    <label
      style={{
        display: 'flex',
        gap: 8,
        backgroundColor: theme.colors.background.secondary,
        border: `2px dashed ${theme.colors.border}`,
        color: theme.colors.text.body,
        padding: 16,
        alignItems: 'center',
        justifyContent: 'center',
        height: 300,
        flexDirection: 'column',
        cursor: 'pointer',
      }}
      {...getRootProps()}
    >
      <input {...getInputProps()} />

      <span style={{ padding: 8 }}>
        {file ? 'Presionar o arrastrar para cambiar el archivo: ' : 'Presionar o arrastrar para subir un: '}
        {acceptFileTypes[contentType].label}
      </span>
      {file && contentType === 'IMAGE' && (
        <img
          src={typeof file === 'string' ? file : URL.createObjectURL(file)}
          alt="submitFile"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {file && contentType === 'AUDIO' && (
        <audio
          controls
          src={typeof file === 'string' ? file : URL.createObjectURL(file)}
          style={{ width: '100%' }}
        />
      )}
      {file && contentType === 'VIDEO' && (
        <video
          controls
          style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'black' }}
          src={typeof file === 'string' ? file : URL.createObjectURL(file)}
        />
      )}
    </label>
  );
};
